import { ReactNode, useState, useEffect } from 'react';
import { useFamilyStore } from '../store/familyStore';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { decodeJWT } from '../utils/jwt';

interface NavBarProps {
  children: ReactNode;
  className?: string;
  isDarkMode?: boolean; // Deprecated - kept for backward compatibility
  containerClassName?: string;
}

export default function NavBar({ 
  children, 
  className = '', 
  isDarkMode: deprecatedIsDarkMode = false,
  containerClassName = ''
}: NavBarProps) {
  const { isDarkMode, toggleTheme } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [time, setTime] = useState(new Date());
  const { family } = useFamilyStore();
  const { idToken, isFamilyMode, exitFamilyMode } = useAuthStore();

  // Get current user ID from JWT token
  const getCurrentUserId = (): string | null => {
    if (!idToken) return null;
    try {
      const decoded = decodeJWT(idToken);
      return decoded?.sub || null;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();
  
  // Find the logged-in user in the family members list
  const currentUserMember = currentUserId 
    ? family?.members.find(member => member.userId === currentUserId)
    : null;
  
  // Prefer logged-in user's identity from idToken; fall back to current user member, then owner, then first member
  const decoded = idToken ? decodeJWT(idToken) : null;
  const authName: string | undefined = decoded?.name || decoded?.email || decoded?.preferred_username;

  const fallbackMember = currentUserMember || family?.members.find(member => member.role === 'owner') || family?.members?.[0];

  const displayName = authName || currentUserMember?.name || fallbackMember?.name;
  
  // Get current user from family members
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const hours24 = time.getHours();
  const minutes = time.getMinutes();

  // Convert to 12-hour format
  const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
  const ampm = hours24 >= 12 ? 'PM' : 'AM';

  // Format time with leading zeros
  const formatTime = (num: number) => num.toString().padStart(2, '0');

  // Extract padding from containerClassName to match clock padding
  // Default to px-2 sm:px-3 if no padding specified
  const hasPadding = containerClassName.includes('px-');
  const defaultPadding = hasPadding ? '' : 'px-2 sm:px-3';
  
  // Base box styling - extract from className or use default
  // If className is provided, use it; otherwise use default solid background
  const baseBoxStyle = className 
    ? className 
    : `${tokens.card.bg} ${tokens.card.shadow} ${tokens.card.border} border`;
  
  return (
    <nav className="m-2">
      <div className={`flex items-center justify-between gap-3 sm:gap-4 rounded-landing ${containerClassName || defaultPadding} ${baseBoxStyle} min-h-[56px] sm:min-h-[64px] transition-colors duration-300`}>
        {/* Left: Page Title/Content */}
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
          <div className="min-w-0 flex-1">{children}</div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          {isFamilyMode && (
            <button
              type="button"
              onClick={exitFamilyMode}
              className={`min-h-[44px] min-w-[44px] sm:min-h-[48px] sm:min-w-0 sm:px-3 rounded-landing text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${tokens.text.muted} ${tokens.border.light} border ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}
              aria-label="Exit family device mode"
              title="Exit family device mode"
            >
              <span className="hidden sm:inline">Exit family mode</span>
              <span className="sm:hidden" aria-hidden>Exit</span>
            </button>
          )}
          {/* User Info - Compact Display */}
          <div className="hidden md:flex items-center gap-3 pr-2">
            <div className="text-right">
              <div className={`${tokens.text.primary} text-sm font-semibold leading-tight`}>
              {displayName || 'User'}
            </div>
              <div className={`${tokens.text.muted} text-xs leading-tight`} style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
              {hours12}:{formatTime(minutes)} {ampm}
              </div>
            </div>
          </div>

          {/* Theme Toggle Button - Matching landing page style (fully round) */}
          <button
            onClick={toggleTheme}
            className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 active:scale-95 flex-shrink-0 group"
            style={{
              background: isDarkMode 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.04)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
              boxShadow: isDarkMode 
                ? '0 18px 40px rgba(0, 0, 0, 0.28)' 
                : '0 18px 40px rgba(141, 125, 107, 0.12)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDarkMode 
                ? 'rgba(255, 255, 255, 0.08)' 
                : 'rgba(0, 0, 0, 0.06)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDarkMode 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(0, 0, 0, 0.04)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            aria-label="Toggle theme"
            title="Switch between light and dark theme"
          >
            <span className="text-lg sm:text-xl relative w-5 h-5 flex items-center justify-center">
              <span 
                className={`absolute transition-all duration-300 ${isDarkMode ? 'opacity-0 scale-0 rotate-90' : 'opacity-100 scale-100 rotate-0'}`}
              >
                ☀️
              </span>
              <span 
                className={`absolute transition-all duration-300 ${isDarkMode ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-0 -rotate-90'}`}
              >
                🌙
              </span>
            </span>
          </button>
        </div>
      </div>
    </nav>
  );
}
